import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import { apiClient } from '../../services/apiClient'

interface Withdrawal {
  id: string
  user_id: string
  user_name: string
  user_email: string
  amount: number
  fee: number
  net_amount: number
  status: string
  finance_approved_by?: string
  finance_approved_at?: string
  audit_approved_by?: string
  audit_approved_at?: string
  rejected_by?: string
  rejected_at?: string
  rejection_reason?: string
  created_at: string
  operator_code?: string
  operator_name?: string
}

const statusLabels = {
  pending: '待财务审核',
  finance_approved: '待审计审核',
  audit_approved: '待打款',
  completed: '已完成',
  rejected: '已拒绝'
}

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  finance_approved: 'bg-blue-100 text-blue-800',
  audit_approved: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
}

export default function WithdrawalsPage() {
  const navigate = useNavigate()
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [activeTab, setActiveTab] = useState<'user' | 'operator'>('user') // 新增：标签页状态
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    has_more: false
  })

  const fetchWithdrawals = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        order_type: activeTab === 'user' ? '1' : '2', // 1=用户提现, 2=运营商提现
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        sort_by: 'created_at',
        sort_order: 'desc'
      })

      const response = await apiClient.getWithdrawalsList(params)
      
      if (response.success && response.data) {
        setWithdrawals(response.data.withdrawals)
        setPagination(response.data.pagination)
      } else {
        setError(response.message || '获取提现订单列表失败')
      }
    } catch (err) {
      setError('网络错误，请稍后重试')
      console.error('Fetch withdrawals error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWithdrawals()
  }, [currentPage, searchTerm, statusFilter, dateFrom, dateTo, activeTab]) // 添加 activeTab 依赖

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchWithdrawals()
  }

  const handleWithdrawalClick = (withdrawalId: string) => {
    navigate(`/withdrawals/${withdrawalId}`)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'finance_approved':
        return <ClockIcon className="h-4 w-4" />
      case 'audit_approved':
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'rejected':
        return <XCircleIcon className="h-4 w-4" />
      default:
        return <ClockIcon className="h-4 w-4" />
    }
  }

  if (loading && withdrawals.length === 0) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="page-title">提现审核</h1>
        <p className="page-subtitle">审核用户和运营商的提现申请</p>
      </div>

      {/* 标签页 */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('user')
                setCurrentPage(1)
              }}
              className={`${
                activeTab === 'user'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              用户提现
            </button>
            <button
              onClick={() => {
                setActiveTab('operator')
                setCurrentPage(1)
              }}
              className={`${
                activeTab === 'operator'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              运营商提现
            </button>
          </nav>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="card mb-6">
        <div className="card-body">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {activeTab === 'user' ? '搜索用户' : '搜索运营商'}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={activeTab === 'user' ? '用户姓名或邮箱' : '运营商姓名或邮箱'}
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  审批状态
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">全部状态</option>
                  <option value="pending">待财务审核</option>
                  <option value="finance_approved">待审计审核</option>
                  <option value="audit_approved">待打款</option>
                  <option value="completed">已完成</option>
                  <option value="rejected">已拒绝</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  开始日期
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  结束日期
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full btn btn-secondary"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  筛选
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {/* 提现订单列表 */}
      <div className="card">
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {withdrawals.map((withdrawal) => (
              <li key={withdrawal.id}>
                <div
                  onClick={() => handleWithdrawalClick(withdrawal.id)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {withdrawal.user_name}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            statusColors[withdrawal.status as keyof typeof statusColors]
                          }`}>
                            {getStatusIcon(withdrawal.status)}
                            <span className="ml-1">{statusLabels[withdrawal.status as keyof typeof statusLabels]}</span>
                          </span>
                        </div>
                        <div className="flex items-center mt-1 space-x-4">
                          <p className="text-sm text-gray-500">{withdrawal.user_email}</p>
                          {withdrawal.operator_code && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {withdrawal.operator_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        申请金额: {formatCurrency(withdrawal.amount)}
                      </div>
                      <div className="text-sm text-gray-500">
                        手续费: {formatCurrency(withdrawal.fee)}
                      </div>
                      <div className="text-sm font-medium text-blue-600">
                        实际到账: {formatCurrency(withdrawal.net_amount)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        申请时间: {formatDate(withdrawal.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {withdrawals.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">暂无提现订单</p>
            </div>
          )}
        </div>

        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 20, pagination.total)}
                  </span>{' '}
                  条，共 <span className="font-medium">{pagination.total}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {currentPage} / {pagination.pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}